import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import Actions from "../Redux/action";
import {
    getCameraAlignmentCamerasLoadingSelector,
    getCameraAlignmentCamerasSelector,
    getCameraAlignmentCooldownSelector,
    getCameraAlignmentListSelector,
    getCameraAlignmentLoadingSelector,
    getCameraAlignmentRequestingSelector,
    getCameraAlignmentTotalCountSelector,
} from "../Redux/reducer/CameraAlignmentReducer";
import { getHostelListSelectSelector } from "../Redux/reducer/HostelReducer";
import { getUserData } from "../Redux/reducer/LoginReducer";
import { BASE_URL } from "../Redux/saga/EndPoints";
import AppLoader from "../component/AppLoader";
import { PaginationComponent } from "../component/PaginationComponent";
import { ViewNotificationImagePopup } from "../component/ViewNotificationImage";
import nodatafound from "../asset/img/datanotfound_black.png";

const PAGE_SIZE = 10;

const formatCapturedAt = (value) => {
    if (!value) return "-";
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
};

const resolveImageUrl = (item) => {
    const imageUrl = item?.image_url || item?.latest_alignment?.image_url;
    const imagePath = item?.image_path || item?.latest_alignment?.image_path;
    if (imageUrl) {
        return imageUrl.startsWith("http")
            ? imageUrl
            : `${BASE_URL.replace(/\/$/, "")}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
    }
    if (imagePath) {
        return `${BASE_URL.replace(/\/$/, "")}/camera/alignment/image?img_path=${encodeURIComponent(imagePath)}`;
    }
    return null;
};

export const CameraAlignment = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(getUserData);
    const alignmentList = useSelector(getCameraAlignmentListSelector);
    const loading = useSelector(getCameraAlignmentLoadingSelector);
    const totalCount = useSelector(getCameraAlignmentTotalCountSelector);
    const cameras = useSelector(getCameraAlignmentCamerasSelector);
    const camerasLoading = useSelector(getCameraAlignmentCamerasLoadingSelector);
    const requesting = useSelector(getCameraAlignmentRequestingSelector);
    const cooldownMinutes = useSelector(getCameraAlignmentCooldownSelector);
    const hostelList = useSelector(getHostelListSelectSelector);

    const [hostelFilterId, setHostelFilterId] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [popup, setPopup] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [checkingCameraId, setCheckingCameraId] = useState(null);

    const isAdminOrClientAdmin = user?.user_type === "0" || user?.user_type === "6";

    useEffect(() => {
        if (!isAdminOrClientAdmin) {
            navigate("/dashboard");
        }
    }, [isAdminOrClientAdmin, navigate]);

    const fetchListForPage = useCallback(
        (page) => {
            const params = { page, page_size: PAGE_SIZE };
            if (hostelFilterId) params.hostel_id = hostelFilterId;
            dispatch(Actions.cameraAlignmentList(params));
        },
        [dispatch, hostelFilterId]
    );

    const fetchCameras = useCallback(() => {
        if (!hostelFilterId) return;
        dispatch(Actions.cameraAlignmentCameras({ hostel_id: hostelFilterId }));
    }, [dispatch, hostelFilterId]);

    useEffect(() => {
        if (!isAdminOrClientAdmin) return;
        dispatch(Actions.hostelListSelect({}));
    }, [dispatch, isAdminOrClientAdmin]);

    useEffect(() => {
        if (!isAdminOrClientAdmin) return;
        fetchListForPage(pageNumber);
    }, [isAdminOrClientAdmin, fetchListForPage, pageNumber]);

    useEffect(() => {
        if (!isAdminOrClientAdmin) return;
        if (hostelFilterId) fetchCameras();
    }, [isAdminOrClientAdmin, hostelFilterId, fetchCameras]);

    const handleHostelFilterChange = (e) => {
        setHostelFilterId(e.target.value);
        setPageNumber(1);
    };

    const onChangeHandle = (page) => {
        setPageNumber(page);
    };

    const openImage = (item) => {
        const url = resolveImageUrl(item);
        if (!url) return;
        setPopupData(url);
        setPopup(true);
    };

    const handleCheck = (camera) => {
        if (!hostelFilterId) {
            toast.error("Select a hostel first");
            return;
        }
        if (!camera?.can_check) {
            const wait = camera?.wait_minutes || cooldownMinutes;
            toast.warning(
                `You already raised a check. Please wait ${wait} minute(s).`
            );
            return;
        }

        setCheckingCameraId(camera.camera_id);
        dispatch(
            Actions.cameraAlignmentRequest(
                { camera_id: camera.camera_id, hostel_id: Number(hostelFilterId) },
                (res) => {
                    setCheckingCameraId(null);
                    const statusCode = res?.status_code;
                    const apiMessage = res?.message || res?.response?.data?.message;
                    if (statusCode === 0) {
                        toast.success("Check requested. Snapshot will appear after the next worker poll.");
                        fetchCameras();
                        return;
                    }
                    toast.warning(
                        apiMessage ||
                            `You already raised a check. Please wait ${cooldownMinutes} minute(s).`
                    );
                    fetchCameras();
                }
            )
        );
    };

    if (!isAdminOrClientAdmin) return null;

    return (
        <div className="ecommerce-widget">
            <h5 className="card-header">
                <i className="fa-solid fa-camera me-2" aria-hidden="true" />
                Camera Alignment
            </h5>
            <p className="text-muted small mb-0 px-3 pt-1">
                Raise a check per camera. The edge worker polls about every {cooldownMinutes} minutes,
                captures with ROI, and uploads the snapshot here.
            </p>

            <div className="card border-3 border-top border-top-primary mb-3">
                <div className="card-body py-3">
                    <div className="row align-items-end g-3">
                        <div className="col-lg-3 col-md-4">
                            <label className="form-label small text-muted mb-1">Filter by hostel</label>
                            <select
                                className="form-select form-select-sm"
                                value={hostelFilterId}
                                onChange={handleHostelFilterChange}
                            >
                                <option value="">Select hostel</option>
                                {Array.isArray(hostelList) &&
                                    hostelList.map((h) => (
                                        <option key={h.id} value={h.id}>
                                            {h.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <h6 className="px-1 mb-2">Cameras</h6>
            {!hostelFilterId ? (
                <p className="text-muted px-1">Select a hostel to load cameras and raise checks.</p>
            ) : camerasLoading ? (
                <AppLoader />
            ) : Array.isArray(cameras) && cameras.length > 0 ? (
                <div className="row g-3 px-1 mb-4">
                    {cameras.map((camera) => {
                        const imgUrl = resolveImageUrl(camera);
                        const isBusy = requesting && checkingCameraId === camera.camera_id;
                        return (
                            <div className="col-xl-3 col-lg-4 col-md-6" key={camera.camera_id}>
                                <div className="card h-100 shadow-sm">
                                    <div
                                        className="bg-light d-flex align-items-center justify-content-center"
                                        style={{ height: 160, cursor: imgUrl ? "pointer" : "default" }}
                                        onClick={() => imgUrl && openImage(camera)}
                                    >
                                        {imgUrl ? (
                                            <img
                                                src={imgUrl}
                                                alt={camera.camera_name || "camera"}
                                                style={{
                                                    maxHeight: "100%",
                                                    maxWidth: "100%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        ) : (
                                            <span className="text-muted small">No snapshot yet</span>
                                        )}
                                    </div>
                                    <div className="card-body py-2">
                                        <h6 className="card-title mb-1 text-truncate" title={camera.camera_name}>
                                            {camera.camera_name || "-"}
                                        </h6>
                                        <p className="card-text small text-muted mb-2">
                                            {camera.open_request_status
                                                ? `Status: ${camera.open_request_status}`
                                                : camera.latest_alignment?.captured_at
                                                    ? `Last: ${formatCapturedAt(camera.latest_alignment.captured_at)}`
                                                    : "No open request"}
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary w-100"
                                            disabled={isBusy || !camera.can_check}
                                            onClick={() => handleCheck(camera)}
                                            title={
                                                camera.can_check
                                                    ? "Request alignment snapshot"
                                                    : `Wait ${camera.wait_minutes || cooldownMinutes} minute(s)`
                                            }
                                        >
                                            {isBusy
                                                ? "Requesting..."
                                                : camera.can_check
                                                    ? "Check"
                                                    : `Wait ${camera.wait_minutes || cooldownMinutes}m`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-4 mb-3">
                    <img src={nodatafound} alt="No data" style={{ maxWidth: 180 }} />
                    <p className="text-muted mt-2 mb-0">No cameras mapped to this hostel</p>
                </div>
            )}

            <h6 className="px-1 mb-2">Recent snapshots</h6>
            {loading ? (
                <AppLoader />
            ) : Array.isArray(alignmentList) && alignmentList.length > 0 ? (
                <>
                    <div className="row g-3 px-1">
                        {alignmentList.map((item) => {
                            const imgUrl = resolveImageUrl(item);
                            return (
                                <div className="col-xl-3 col-lg-4 col-md-6" key={item.id}>
                                    <div className="card h-100 shadow-sm">
                                        <div
                                            className="bg-light d-flex align-items-center justify-content-center"
                                            style={{ height: 180, cursor: imgUrl ? "pointer" : "default" }}
                                            onClick={() => openImage(item)}
                                        >
                                            {imgUrl ? (
                                                <img
                                                    src={imgUrl}
                                                    alt={item.camera_name || "alignment"}
                                                    style={{
                                                        maxHeight: "100%",
                                                        maxWidth: "100%",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            ) : (
                                                <span className="text-muted small">No image</span>
                                            )}
                                        </div>
                                        <div className="card-body py-2">
                                            <h6 className="card-title mb-1 text-truncate" title={item.camera_name}>
                                                {item.camera_name || "-"}
                                            </h6>
                                            <p className="card-text small text-muted mb-1">
                                                Hostel: {item.hostel_name || "-"}
                                            </p>
                                            <p className="card-text small mb-0">
                                                {formatCapturedAt(item.captured_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {totalCount > PAGE_SIZE && (
                        <div className="mt-3">
                            <PaginationComponent
                                onChangeHandle={onChangeHandle}
                                pageCount={totalCount}
                                pageNumber={pageNumber}
                                setpage={setPageNumber}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-5">
                    <img src={nodatafound} alt="No data" style={{ maxWidth: 220 }} />
                    <p className="text-muted mt-2 mb-0">No camera alignment images found</p>
                </div>
            )}

            {popup && (
                <ViewNotificationImagePopup
                    onClose={() => {
                        setPopup(false);
                        setPopupData(null);
                    }}
                    data={popupData}
                />
            )}
        </div>
    );
};

export default CameraAlignment;
